import { useState, useRef, FormEvent } from 'react';
import {
  SyphraxSettings, Visibility, ProfileFields, PrivacyFields,
  TTL_OPTIONS, TIMEOUT_OPTIONS, defaultSettings,
} from '../storage';
import { api } from '../api';
import * as crypto from '../crypto';
import type { Session } from './Auth';
import * as recovery from '../recovery_phrase';
import RecoveryPhraseModal from '../components/RecoveryPhraseModal';
import { LOCALES, getLocale, setLocale, t, useTranslation } from '../i18n';

type Tab = 'profile' | 'privacy' | 'messages' | 'blocked' | 'notifications' | 'session' | 'account' | 'language' | 'about';

interface Props {
  session: Session;
  settings: SyphraxSettings;
  activeFrequency: string | null;
  onChange: (next: SyphraxSettings) => void;
  onClose: () => void;
  onAfterPasswordChanged: (newPrivateKey: Uint8Array) => void;
}

function visOptions(): Array<{ value: Visibility; label: string }> {
  return [
    { value: 'all', label: t('settings.vis.all') },
    { value: 'contacts', label: t('settings.vis.contacts') },
    { value: 'none', label: t('settings.vis.none') },
  ];
}

export default function Settings({ session, settings, activeFrequency, onChange, onClose, onAfterPasswordChanged }: Props) {
  useTranslation();
  const [tab, setTab] = useState<Tab>('profile');
  const [scope, setScope] = useState<'global' | 'frequency'>(activeFrequency ? 'frequency' : 'global');

  function patch(p: Partial<SyphraxSettings>) {
    onChange({ ...settings, ...p });
  }

  return (
    <div className="settings-screen">
      <header className="settings-header">
        <button className="ghost" onClick={onClose}>{'← ' + t('settings.close')}</button>
        <h1>{t('settings.title')}</h1>
        <div />
      </header>
      <div className="settings-body">
        <nav className="settings-nav">
          {[
            ['profile', t('settings.tab.profile')],
            ['privacy', t('settings.tab.privacy')],
            ['messages', t('settings.tab.messages')],
            ['blocked', t('settings.tab.blocked')],
            ['notifications', t('settings.tab.notifications')],
            ['session', t('settings.tab.session')],
            ['account', t('settings.tab.account')],
            ['language', t('settings.tab.language')],
            ['about', t('settings.tab.about')],
          ].map(([k, label]) => (
            <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k as Tab)}>{label}</button>
          ))}
        </nav>
        <section className="settings-content">
          {(tab === 'profile' || tab === 'privacy') && activeFrequency && (
            <div className="scope-toggle">
              <button className={scope === 'global' ? 'active' : ''} onClick={() => setScope('global')}>
                {t('settings.scope.global')}
              </button>
              <button className={scope === 'frequency' ? 'active' : ''} onClick={() => setScope('frequency')}>
                {t('settings.scope.freq', activeFrequency)}
              </button>
            </div>
          )}

          {tab === 'profile' && (
            <ProfileSection
              settings={settings}
              scope={scope}
              activeFrequency={activeFrequency}
              onChange={patch}
            />
          )}
          {tab === 'privacy' && (
            <PrivacySection
              settings={settings}
              scope={scope}
              activeFrequency={activeFrequency}
              onChange={patch}
            />
          )}
          {tab === 'messages' && <MessagesSection settings={settings} onChange={patch} />}
          {tab === 'blocked' && <BlockedSection settings={settings} onChange={patch} />}
          {tab === 'notifications' && <NotificationsSection settings={settings} onChange={patch} />}
          {tab === 'session' && <SessionSection settings={settings} onChange={patch} />}
          {tab === 'account' && (
            <AccountSection
              session={session}
              settings={settings}
              onChange={patch}
              onAfterPasswordChanged={onAfterPasswordChanged}
            />
          )}
          {tab === 'language' && (
            <LanguageSection
              currentLocale={getLocale()}
              onChange={(loc) => {
                setLocale(loc);
                onChange({ ...settings, language: loc });
              }}
            />
          )}
          {tab === 'about' && <AboutSection />}
        </section>
      </div>
    </div>
  );
}

function ProfileSection({
  settings, scope, activeFrequency, onChange,
}: { settings: SyphraxSettings; scope: 'global' | 'frequency'; activeFrequency: string | null; onChange: (p: Partial<SyphraxSettings>) => void }) {
  const isGlobal = scope === 'global' || !activeFrequency;
  const base: ProfileFields = settings.profile;
  const override = activeFrequency ? settings.profileByFrequency[activeFrequency] ?? {} : {};
  const current: ProfileFields = isGlobal ? base : { ...base, ...override };

  const fileRef = useRef<HTMLInputElement | null>(null);

  function set<K extends keyof ProfileFields>(key: K, value: ProfileFields[K]) {
    if (isGlobal) {
      onChange({ profile: { ...base, [key]: value } });
    } else {
      onChange({
        profileByFrequency: {
          ...settings.profileByFrequency,
          [activeFrequency!]: { ...override, [key]: value },
        },
      });
    }
  }

  function clearOverride() {
    if (!activeFrequency) return;
    const next = { ...settings.profileByFrequency };
    delete next[activeFrequency];
    onChange({ profileByFrequency: next });
  }

  async function pickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      const img = new Image();
      img.onload = () => {
        const size = 256;
        const c = document.createElement('canvas');
        c.width = size; c.height = size;
        const ctx = c.getContext('2d')!;
        const min = Math.min(img.width, img.height);
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
        const out = c.toDataURL('image/jpeg', 0.78);
        set('photoB64', out);
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  }

  return (
    <div className="setting-block">
      <h2>{t('settings.profile.title')}</h2>
      <p className="hint">
        {isGlobal
          ? t('settings.profile.hintGlobal')
          : t('settings.profile.hintFreq', activeFrequency!)}
      </p>

      <label className="field">
        <span>{t('settings.profile.displayName')}</span>
        <input
          type="text"
          value={current.displayName}
          onChange={(e) => set('displayName', e.target.value)}
          placeholder={isGlobal ? t('settings.profile.displayNamePh.global') : t('settings.profile.displayNamePh.freq')}
          maxLength={64}
        />
      </label>

      <div className="field">
        <span>{t('settings.profile.photo')}</span>
        <div className="photo-row">
          <div className="photo-preview">
            {current.photoB64 ? <img src={current.photoB64} alt="avatar" /> : <span>{(current.displayName || '?').slice(0, 1).toUpperCase()}</span>}
          </div>
          <div className="photo-actions">
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={pickPhoto} />
            <button type="button" className="ghost" onClick={() => fileRef.current?.click()}>{t('settings.profile.changePhoto')}</button>
            {current.photoB64 && <button type="button" className="ghost" onClick={() => set('photoB64', null)}>{t('settings.profile.removePhoto')}</button>}
          </div>
        </div>
      </div>

      <label className="field">
        <span>{t('settings.profile.info')}</span>
        <textarea
          rows={3}
          value={current.info}
          onChange={(e) => set('info', e.target.value)}
          placeholder={isGlobal ? t('settings.profile.infoPh') : t('settings.profile.displayNamePh.freq')}
          maxLength={140}
        />
      </label>

      {!isGlobal && Object.keys(override).length > 0 && (
        <button type="button" className="ghost" onClick={clearOverride}>
          {t('settings.profile.removeOverride', activeFrequency!)}
        </button>
      )}

      <p className="warn">{t('settings.profile.warn')}</p>
    </div>
  );
}

function PrivacySection({
  settings, scope, activeFrequency, onChange,
}: { settings: SyphraxSettings; scope: 'global' | 'frequency'; activeFrequency: string | null; onChange: (p: Partial<SyphraxSettings>) => void }) {
  const isGlobal = scope === 'global' || !activeFrequency;
  const base: PrivacyFields = settings.privacy;
  const override = activeFrequency ? settings.privacyByFrequency[activeFrequency] ?? {} : {};
  const current: PrivacyFields = isGlobal ? base : { ...base, ...override };

  function set<K extends keyof PrivacyFields>(key: K, value: PrivacyFields[K]) {
    if (isGlobal) {
      onChange({ privacy: { ...base, [key]: value } });
    } else {
      onChange({
        privacyByFrequency: {
          ...settings.privacyByFrequency,
          [activeFrequency!]: { ...override, [key]: value },
        },
      });
    }
  }

  function visRow(key: keyof PrivacyFields, label: string) {
    return (
      <label className="field">
        <span>{label}</span>
        <select value={current[key] as string} onChange={(e) => set(key, e.target.value as Visibility)}>
          {visOptions().map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
    );
  }

  return (
    <div className="setting-block">
      <h2>{t('settings.privacy.title')}</h2>
      <p className="hint">
        {isGlobal
          ? t('settings.privacy.hintGlobal')
          : t('settings.privacy.hintFreq', activeFrequency!)}
      </p>

      {visRow('showPhoto', t('settings.privacy.showPhoto'))}
      {visRow('showInfo', t('settings.privacy.showInfo'))}
      {visRow('showLastSeen', t('settings.privacy.showLastSeen'))}
      {visRow('showOnline', t('settings.privacy.showOnline'))}
      {visRow('whoAddsMeToGroups', t('settings.privacy.whoAdds'))}

      <label className="field toggle">
        <span>{t('settings.privacy.readReceipts')}</span>
        <input
          type="checkbox"
          checked={current.sendReadReceipts}
          onChange={(e) => set('sendReadReceipts', e.target.checked)}
        />
      </label>
      <p className="hint">{t('settings.privacy.readReceiptsHint')}</p>
    </div>
  );
}

function MessagesSection({ settings, onChange }: { settings: SyphraxSettings; onChange: (p: Partial<SyphraxSettings>) => void }) {
  return (
    <div className="setting-block">
      <h2>{t('settings.messages.title')}</h2>
      <label className="field">
        <span>{t('settings.messages.ttl')}</span>
        <select
          value={settings.messages.defaultTtlSeconds}
          onChange={(e) => onChange({ messages: { defaultTtlSeconds: Number(e.target.value) } })}
        >
          {TTL_OPTIONS.map((o) => <option key={o.seconds} value={o.seconds}>{t(o.labelKey)}</option>)}
        </select>
      </label>
      <p className="hint">{t('settings.messages.ttlHint')}</p>
    </div>
  );
}

function BlockedSection({ settings, onChange }: { settings: SyphraxSettings; onChange: (p: Partial<SyphraxSettings>) => void }) {
  const [add, setAdd] = useState('');
  function block() {
    const v = add.trim().toLowerCase();
    if (!v || settings.blocked.includes(v)) return;
    onChange({ blocked: [...settings.blocked, v] });
    setAdd('');
  }
  function unblock(id: string) {
    onChange({ blocked: settings.blocked.filter((x) => x !== id) });
  }
  return (
    <div className="setting-block">
      <h2>{t('settings.blocked.title')}</h2>
      <p className="hint">{t('settings.blocked.hint')}</p>
      <div className="manual-row">
        <input type="text" placeholder={t('settings.blocked.placeholder')} value={add} onChange={(e) => setAdd(e.target.value)} autoCapitalize="none" autoCorrect="off" />
        <button className="primary" onClick={block} disabled={!add.trim()}>{t('settings.blocked.btn')}</button>
      </div>
      {settings.blocked.length === 0 ? (
        <p className="hint">{t('settings.blocked.empty')}</p>
      ) : (
        <ul className="blocked-list">
          {settings.blocked.map((id) => (
            <li key={id}>
              <span>{id}</span>
              <button className="ghost" onClick={() => unblock(id)}>{t('settings.blocked.unblock')}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NotificationsSection({ settings, onChange }: { settings: SyphraxSettings; onChange: (p: Partial<SyphraxSettings>) => void }) {
  function set<K extends keyof typeof settings.notifications>(key: K, value: boolean) {
    onChange({ notifications: { ...settings.notifications, [key]: value } });
  }
  return (
    <div className="setting-block">
      <h2>{t('settings.notif.title')}</h2>
      <label className="field toggle"><span>{t('settings.notif.sound')}</span>
        <input type="checkbox" checked={settings.notifications.sound} onChange={(e) => set('sound', e.target.checked)} /></label>
      <label className="field toggle"><span>{t('settings.notif.preview')}</span>
        <input type="checkbox" checked={settings.notifications.preview} onChange={(e) => set('preview', e.target.checked)} /></label>
      <label className="field toggle"><span>{t('settings.notif.vibration')}</span>
        <input type="checkbox" checked={settings.notifications.vibration} onChange={(e) => set('vibration', e.target.checked)} /></label>
      <p className="hint">{t('settings.notif.hint')}</p>
    </div>
  );
}

function SessionSection({ settings, onChange }: { settings: SyphraxSettings; onChange: (p: Partial<SyphraxSettings>) => void }) {
  return (
    <div className="setting-block">
      <h2>{t('settings.session.title')}</h2>
      <label className="field">
        <span>{t('settings.session.timeout')}</span>
        <select
          value={settings.session.inactivityTimeoutSeconds}
          onChange={(e) => onChange({ session: { ...settings.session, inactivityTimeoutSeconds: Number(e.target.value) } })}
        >
          {TIMEOUT_OPTIONS.map((o) => <option key={o.seconds} value={o.seconds}>{t(o.labelKey)}</option>)}
        </select>
      </label>
      <p className="hint">{t('settings.session.timeoutHint')}</p>

    </div>
  );
}

function AccountSection({
  session, onAfterPasswordChanged,
}: { session: Session; settings: SyphraxSettings; onChange: (p: Partial<SyphraxSettings>) => void; onAfterPasswordChanged: (newPriv: Uint8Array) => void }) {
  const [showChangePw, setShowChangePw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [oldPw, setOldPw] = useState('');
  const [showRecovery, setShowRecovery] = useState<string | null>(null);
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPw.length < 8) { setError(t('settings.account.errPwShort')); return; }
    if (newPw !== confirmPw) { setError(t('settings.account.errPwMismatch')); return; }
    setBusy(true);
    try {
      await crypto.ready();
      const salt = crypto.newSalt();
      const wrappingKey = crypto.deriveKeyFromPassword(newPw, salt);
      const wrapped = crypto.wrapPrivateKey(session.privateKey, wrappingKey);
      await api.changePassword({
        current_password: oldPw,
        new_password: newPw,
        private_key_encrypted: crypto.b64encode(wrapped.ciphertext),
        private_key_salt: crypto.b64encode(salt),
      });
      setMessage(t('settings.account.success'));
      setShowChangePw(false);
      setOldPw(''); setNewPw(''); setConfirmPw('');
      onAfterPasswordChanged(session.privateKey);
    } catch (err) {
      const m = (err as Error).message;
      if (m.includes('HTTP 401')) setError(t('settings.account.errPwBad'));
      else setError(m);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="setting-block">
      <h2>{t('settings.account.title')}</h2>
      <div className="field">
        <span>{t('settings.account.idLabel')}</span>
        <div className="readonly">{session.syphraxId}</div>
      </div>
      <button className="secondary" style={{ marginBottom: 12 }} onClick={() => {
        const phrase = session.wrappingKey ? recovery.loadRecoveryPhrase(session.wrappingKey) : null;
        if (phrase) setShowRecovery(phrase);
        else setError('No se pudo cargar la clave de recuperacion (cuenta antigua sin BIP39)');
      }}>Ver mi clave de recuperacion (24 palabras)</button>
      {showRecovery && (
        <RecoveryPhraseModal
          phrase={showRecovery}
          mode="show_again"
          onClose={() => setShowRecovery(null)}
        />
      )}
      {!showChangePw ? (
        <button className="primary" onClick={() => setShowChangePw(true)}>{t('settings.account.changePassword')}</button>
      ) : (
        <form onSubmit={changePassword} className="setting-form">
          <label className="field">
            <span>{t('settings.account.currentPw')}</span>
            <input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} required disabled={busy} />
          </label>
          <label className="field">
            <span>{t('settings.account.newPw')}</span>
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={8} disabled={busy} />
          </label>
          <label className="field">
            <span>{t('settings.account.confirmPw')}</span>
            <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required minLength={8} disabled={busy} />
          </label>
          <div className="modal-actions">
            <button type="button" className="ghost" onClick={() => setShowChangePw(false)} disabled={busy}>{t('common.cancel')}</button>
            <button type="submit" className="primary" disabled={busy}>{busy ? t('settings.account.saving') : t('common.confirm')}</button>
          </div>
        </form>
      )}
      {message && <div className="info">{message}</div>}
      {error && <div className="error">{error}</div>}
    </div>
  );
}

function AboutSection() {
  return (
    <div className="setting-block">
      <h2>{t('settings.about.title')}</h2>
      <p className="hint"><i>{t('common.tagline')}</i></p>
      <p className="hint">{t('settings.about.version')}</p>
      <p className="hint">{t('settings.about.tech')}</p>
    </div>
  );
}

export function ensureSettings(s: SyphraxSettings | undefined): SyphraxSettings {
  if (!s) return defaultSettings();
  const d = defaultSettings();
  return {
    ...d,
    ...s,
    profile: { ...d.profile, ...s.profile },
    privacy: { ...d.privacy, ...s.privacy },
    messages: { ...d.messages, ...s.messages },
    notifications: { ...d.notifications, ...s.notifications },
    session: { ...d.session, ...s.session },
    account: { ...d.account, ...s.account },
    profileByFrequency: s.profileByFrequency ?? {},
    privacyByFrequency: s.privacyByFrequency ?? {},
    blocked: s.blocked ?? [],
  };
}

function LanguageSection({ currentLocale, onChange }: { currentLocale: string; onChange: (loc: any) => void }) {
  return (
    <div className="setting-block">
      <h2>{t('settings.language.title')}</h2>
      <p className="hint">{t('settings.language.hint')}</p>
      <div className="lang-grid">
        {LOCALES.map((loc) => (
          <button
            key={loc.code}
            className={'lang-btn' + (loc.code === currentLocale ? ' active' : '')}
            onClick={() => onChange(loc.code)}
          >
            <span className="lang-flag">{loc.flag}</span>
            <span className="lang-name">{loc.native}</span>
            {loc.code === currentLocale && <span className="lang-check">✓</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
